import ContentCopy from "@mui/icons-material/ContentCopy";
import Check from "@mui/icons-material/Check";
import FormatAlignLeft from "@mui/icons-material/FormatAlignLeft";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Alert, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { lintGutter } from "@codemirror/lint";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { linter } from "@codemirror/lint";
import { oneDark } from "@codemirror/theme-one-dark";
import type { Extension } from "@codemirror/state";
import ReactCodeMirror from "@uiw/react-codemirror";
import { useMemo, useState } from "react";

const jsonLinter = linter(jsonParseLinter());

const EXTENSIONS: Extension[] = [json(), lintGutter(), jsonLinter];
const EXTENSIONS_LIGHT: Extension[] = EXTENSIONS;
const EXTENSIONS_DARK: Extension[] = [...EXTENSIONS, oneDark];

type JsonCodeMirrorProps = {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  maxHeight?: string;
  error?: string | null;
  label?: string;
};

export function JsonCodeMirror({
  value,
  onChange,
  readOnly = false,
  maxHeight = "300px",
  error = null,
  label,
}: JsonCodeMirrorProps) {
  const isDark = useTheme().palette.mode === "dark";
  const [copied, setCopied] = useState(false);

  const extensions = useMemo(
    () => (isDark ? EXTENSIONS_DARK : EXTENSIONS_LIGHT),
    [isDark],
  );

  const handleCopy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleFormat = () => {
    try {
      const formatted = JSON.stringify(JSON.parse(value), null, 2);
      onChange(formatted);
    } catch {
      // leave as-is if JSON is invalid
    }
  };

  return (
    <Box>
      {label && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: "var(--theme-text-secondary)", fontWeight: 600, fontSize: "0.75rem" }}
          >
            {label}
          </Typography>
          {!readOnly && (
            <Tooltip title="Format JSON">
              <IconButton aria-label="Format JSON" size="small" onClick={handleFormat} sx={{ p: 0.25 }}>
                <FormatAlignLeft sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
      <Box
        sx={{
          position: "relative",
          border: "1px solid var(--theme-border)",
          borderRadius: 1,
          overflow: "hidden",
          "& .cm-editor": { fontSize: "0.8125rem" },
          "& .cm-editor.cm-focused": { outline: "none" },
          "& .cm-scroller": { fontFamily: "monospace" },
        }}
      >
        <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
          <IconButton
            aria-label={copied ? "Copied!" : "Copy"}
            size="small"
            onClick={handleCopy}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 1,
              p: 0.375,
              color: "var(--theme-text-secondary)",
              bgcolor: "var(--theme-surface)",
              border: "1px solid var(--theme-border)",
              borderRadius: 0.75,
              "&:hover": { bgcolor: "var(--theme-surface-hover)" },
            }}
          >
            {copied ? <Check sx={{ fontSize: 13 }} /> : <ContentCopy sx={{ fontSize: 13 }} />}
          </IconButton>
        </Tooltip>
        <ReactCodeMirror
          value={value}
          onChange={readOnly ? undefined : onChange}
          readOnly={readOnly}
          theme={isDark ? oneDark : "light"}
          extensions={extensions}
          maxHeight={maxHeight}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: false,
            highlightActiveLine: !readOnly,
            highlightSelectionMatches: false,
            searchKeymap: false,
          }}
        />
      </Box>
      {error && (
        <Alert severity="error" icon={<WarningAmber fontSize="inherit" />} sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
